import { useState, useRef, useEffect, useCallback } from "react";
import * as pdfjs from "pdfjs-dist";
import { message } from "antd";
import { useSelector } from "react-redux";
import {app} from "../../../service/serviceLMS"
import axios from "axios";
import { updateContentProgress, markContentAsCompleted } from "../../../service/UserCourseService";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;

export const usePDFViewer = ({ isOpen, fileName, blockTime, isAcknowledged, contentId, courseId, handleClose, onClose, onSuccess, markSidebarItemAcknowledged, }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const fullscreenTimeoutRef = useRef(null);
  let activeRenderTask = null;
  const roles = useSelector((state) => state.auth?.roles ?? []);
  const currentPortal = useSelector((state) => state.auth?.currentPortal);
  const isAdminRole = currentPortal === "admin" && roles.some((r) => ["ADMIN", "ADMIN_SUPPORT"].includes(r));
  const storedUserId = localStorage.getItem("userId");
  const userId = useSelector((state) => state.auth.userId) || storedUserId;

  const [isSubmitting, setIsSubmitting]         = useState(false);
  const [numPages, setNumPages]                 = useState(null);
  const [currentPage, setCurrentPage]           = useState(1);
  const [isFullScreen, setIsFullScreen]         = useState(false);
  const [remainingTime, setRemainingTime]       = useState(blockTime);
  const [timerActive, setTimerActive]           = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl]             = useState(null);
  const [loading, setLoading]                   = useState(false);
  const [loadingProgress, setLoadingProgress]   = useState(0);
  const [error, setError]                       = useState(null);
  const [pdfFullyLoaded, setPdfFullyLoaded]     = useState(false);
  const [isExitingFullscreen, setIsExitingFullscreen] = useState(false);
  const [hasReachedLastPage, setHasReachedLastPage]   = useState(false);
  const [hasAgreed, setHasAgreed]               = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closeConfirmType, setCloseConfirmType] = useState('');
  const [nextTimer, setNextTimer]               = useState(10);
  const [pageTimers, setPageTimers]             = useState({});
  const [nextTimerActive, setNextTimerActive]   = useState(true);

  const getCleanFilename = (fullFilename) => {
    if (!fullFilename) return '';
    const idx = fullFilename.indexOf('_');
    return idx === -1 ? fullFilename : fullFilename.substring(idx + 1);
  };
  const displayName = getCleanFilename(fileName);

  const closeModal = useCallback(() => {
    const fn = handleClose || onClose;
    if (typeof fn === 'function') fn();
  }, [handleClose, onClose]);

  const updateProgress = useCallback((pageNumber) => {
     if (isAdminRole || isAcknowledged) return Promise.resolve();
    if (pageNumber > 0 && numPages > 0 && userId && contentId && courseId) {
      let pct = (pageNumber / numPages) * 100;
      if (pageNumber === numPages || pct >= 95) pct = 100;
      return updateContentProgress(userId, contentId, courseId, pageNumber, pct, "pdf");
    }
    return Promise.resolve();
  }, [numPages, userId, contentId, courseId]);

  const renderPage = useCallback(async (pdf, pageNumber) => {
    try {
      const page = await pdf.getPage(pageNumber);
      const container = containerRef.current;
      const modalWidth = isFullScreen
        ? window.innerWidth * 0.95
        : container ? container.clientWidth * 0.8 : window.innerWidth * 0.6;

      const viewport = page.getViewport({
        scale: modalWidth / page.getViewport({ scale: 1 }).width,
      });

      const canvas = canvasRef.current;
      if (!canvas) return;
      const context = canvas.getContext("2d");

      if (activeRenderTask) activeRenderTask.cancel();
      context.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      activeRenderTask = page.render({ canvasContext: context, viewport });
      await activeRenderTask.promise;
      activeRenderTask = null;

      if (pageNumber === numPages) setHasReachedLastPage(true);
    } catch (err) {
      if (err.name !== 'RenderingCancelledException') console.error("Render error:", err);
    }
  }, [isFullScreen, numPages]);

  const loadPDF = useCallback(async (blobUrl) => {
    try {
      if (!blobUrl) return;
      const pdf = await pdfjs.getDocument({ url: blobUrl }).promise;
      setNumPages(pdf.numPages);
      await renderPage(pdf, currentPage);
      setPdfFullyLoaded(true);
    } catch {
      setError("Failed to render PDF document");
      setPdfFullyLoaded(false);
    }
  }, [currentPage, renderPage]);

  const toggleFullScreen = async () => {
    try {
      if (!document.fullscreenElement && containerRef.current) {
        await containerRef.current.requestFullscreen();
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error("Fullscreen error:", err);
      message.error("Fullscreen mode not supported");
    }
  };

  const handleModalClose = () => {
  if (isAdminRole) {
    handleConfirmClose();
    return;
  }

  if (isExitingFullscreen) return;
  
  setCloseConfirmType(!isAcknowledged ? (!hasAgreed ? 'no-agreement' : 'general') : 'acknowledged');
  setShowCloseConfirm(true);
};

 const handleConfirmClose = async () => {
  setShowCloseConfirm(false);
  let wasCompleted = false;
  if (numPages && currentPage && userId && contentId && courseId) {
    try {
      await updateProgress(currentPage);
      if (hasAgreed || isAcknowledged) {
        await markContentAsCompleted(courseId, contentId, userId);
        message.success("Course content completed successfully!");
        wasCompleted = true;
      }
    } catch (err) {
      console.error("Progress save failed on close:", err);
    }
  }
  if (wasCompleted && typeof onSuccess === "function") {

      onSuccess();
      markSidebarItemAcknowledged(contentId);
    } else {
      closeModal();
    }
};

  const handleCancelClose = () => setShowCloseConfirm(false);

  const handleFinalSubmit = async (page) => {
    if (!hasAgreed) return;
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await updateProgress(page);
      await markContentAsCompleted(courseId, contentId, userId);
      message.success("Course content completed successfully!");
      if (typeof onSuccess === "function") {
        onSuccess();
        markSidebarItemAcknowledged(contentId);
      } else {
        closeModal();
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to submit progress.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const retryLoading = () => {
    setError(null);
    setPdfBlobUrl(null);
    setLoading(false);
    setPdfFullyLoaded(false);
  };

  const isCloseDisabled = isAdminRole 
  ? false 
  : isAcknowledged
    ? isExitingFullscreen
    : (remainingTime > 0 || isExitingFullscreen);

  
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setRemainingTime(blockTime);
      setTimerActive(false);
      setPdfFullyLoaded(false);
      setError(null);
      setIsExitingFullscreen(false);
      setHasReachedLastPage(false);
      setHasAgreed(isAcknowledged || false);
    } else {
      setTimerActive(false);
      setPdfFullyLoaded(false);
      setIsExitingFullscreen(false);
      if (fullscreenTimeoutRef.current) clearTimeout(fullscreenTimeoutRef.current);
    }
  }, [isOpen, blockTime, isAcknowledged]);


  useEffect(() => {
    if (isOpen && pdfFullyLoaded && !error) {
      if (isAcknowledged) {
        setNextTimer(0); setNextTimerActive(false);
      } else if (!pageTimers[currentPage]) {
        setNextTimer(10); setNextTimerActive(true);
      } else {
        setNextTimer(0); setNextTimerActive(false);
      }
      setTimerActive(true);
    } else {
      setTimerActive(false);
    }
  }, [isOpen, pdfFullyLoaded, error, currentPage, pageTimers, isAcknowledged]);


  useEffect(() => {
    let t;
    if (nextTimerActive && nextTimer > 0) {
      t = setInterval(() => setNextTimer((p) => Math.max(0, p - 1)), 1000);
    } else if (nextTimer === 0 && nextTimerActive) {
      setNextTimerActive(false);
      setPageTimers((p) => ({ ...p, [currentPage]: true }));
    }
    return () => clearInterval(t);
  }, [nextTimerActive, nextTimer, currentPage]);

  useEffect(() => {
    const handler = () => {
      const nowFull = !!document.fullscreenElement;
      if (isFullScreen && !nowFull) {
        setIsExitingFullscreen(true);
        fullscreenTimeoutRef.current = setTimeout(() => {
          setIsExitingFullscreen(false);
          setIsFullScreen(false);
          if (pdfBlobUrl && currentPage) setTimeout(() => loadPDF(pdfBlobUrl), 100);
        }, 300);
      } else {
        setIsFullScreen(nowFull);
        setIsExitingFullscreen(false);
      }
    };
    document.addEventListener("fullscreenchange", handler);
    return () => {
      document.removeEventListener("fullscreenchange", handler);
      if (fullscreenTimeoutRef.current) clearTimeout(fullscreenTimeoutRef.current);
    };
  }, [isFullScreen, pdfBlobUrl, currentPage, loadPDF]);

 
  useEffect(() => {
  const fetchPdf = async () => {
    if (!fileName || !isOpen) return;
    setLoading(true);
    setLoadingProgress(0);
    setError(null);
    setPdfFullyLoaded(false);
    if (pdfBlobUrl) { URL.revokeObjectURL(pdfBlobUrl); setPdfBlobUrl(null); }

    try {
      const res = await app.get(
        `/course/api/client-api/streaming/pdf/${fileName}`,
        {
          responseType: "blob",
          headers: { Accept: "application/pdf", "Cache-Control": "no-cache" },
          timeout: 60000,
          onDownloadProgress: (e) => {
            if (e.total) setLoadingProgress(Math.round((e.loaded / e.total) * 100));
          },
        }
      );

      const blob = new Blob([res.data], { type: "application/pdf" });
      setPdfBlobUrl(URL.createObjectURL(blob));
      setLoadingProgress(100);
    } catch (err) {
      const msg =
        err.code === "ECONNABORTED" ? "Request timeout."
        : err.response?.status === 404 ? "PDF file not found."
        : "Failed to load PDF file.";
      setError(msg);
      message.error(msg);
      setPdfFullyLoaded(false);
    } finally {
      setLoading(false);
      setLoadingProgress(0);
    }
  };
  fetchPdf();
}, [fileName, isOpen]);


  useEffect(() => () => { if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl); }, [pdfBlobUrl]);


  useEffect(() => {
    const handler = () => {
      if (pdfFullyLoaded && !isExitingFullscreen)
        setTimerActive(document.visibilityState === "visible");
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [pdfFullyLoaded, isExitingFullscreen]);

  
  useEffect(() => {
    let t;
    if (timerActive && remainingTime > 0 && !isExitingFullscreen)
      t = setInterval(() => setRemainingTime((p) => Math.max(0, p - 1)), 1000);
    return () => clearInterval(t);
  }, [timerActive, remainingTime, isExitingFullscreen]);


  useEffect(() => { if (pdfBlobUrl && !loading) loadPDF(pdfBlobUrl); }, [pdfBlobUrl, loading]);
  useEffect(() => { if (numPages && pdfBlobUrl && !loading) loadPDF(pdfBlobUrl); }, [currentPage]);

  const saveProgress = () => {
    if (isAdminRole || isAcknowledged) return;
    if (numPages && currentPage) {
      let pct = (currentPage / numPages) * 100;
      if (pct === 100 || numPages - currentPage === 0) pct = 100;
      try { updateContentProgress(userId, contentId, courseId, currentPage, pct, "pdf"); }
      catch (err) { console.error("Error saving PDF progress:", err); }
    }
  };

  return {
    canvasRef, containerRef,
    numPages, currentPage, isFullScreen, remainingTime,
    pdfBlobUrl, loading, loadingProgress, error, pdfFullyLoaded,
    isExitingFullscreen, hasReachedLastPage, hasAgreed,
    showCloseConfirm, closeConfirmType,
    nextTimer, nextTimerActive, isSubmitting,
    displayName, isCloseDisabled, userId,
    toggleFullScreen,
    handleModalClose, handleConfirmClose, handleCancelClose,
    handleFinalSubmit, retryLoading,
    setHasAgreed,
    nextPage: () => { if (currentPage < numPages) setCurrentPage((p) => p + 1); },
    prevPage: () => { if (currentPage > 1) setCurrentPage((p) => p - 1); },
    saveProgress,
  };
};